import { Link } from "react-router";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { formatDate, formatMoney, useSubscriptions, type UserSubscription } from "@/lib/subscriptions";
import { MmCard, MmPage, periodLabel } from "./shared";

/** MindfulMe: one card per subscription (main plan + coaching), each with its own cancel link. */
export function ManageSubscription() {
  const { t, language } = useTranslation();
  const { data: subs, isLoading, isError } = useSubscriptions();

  return (
    <MmPage backTo="/profile" backLabel={t("mm_back")} title={t("mm_manage_sub")}>
      {isLoading && <p className="mt-6 text-sm text-slate-500">{t("profile_loading")}</p>}
      {isError && <p className="mt-6 text-sm text-slate-500">{t("mm_subs_error")}</p>}
      {subs && subs.length === 0 && <p className="mt-6 text-base text-slate-600">{t("mm_no_subs")}</p>}

      {subs?.map((sub) => (
        <SubscriptionBlock key={sub.id} sub={sub} t={t} language={language} />
      ))}
    </MmPage>
  );
}

function SubscriptionBlock({
  sub,
  t,
  language,
}: {
  sub: UserSubscription;
  t: ReturnType<typeof useTranslation>["t"];
  language: string;
}) {
  const cancelled = sub.cancelAtPeriodEnd || sub.status === "canceled";
  return (
    <div className="mt-6">
      <MmCard className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-slate-500">{t("mm_current_plan")}</p>
            <p className="mt-0.5 text-xl font-bold text-slate-900">
              {sub.kind === "coach" ? t("mm_plan_coach") : sub.kind === "main" ? t("mm_plan_main") : sub.planName}
            </p>
          </div>
          <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${
              cancelled ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-800"
            }`}
          >
            <span className={`size-2 rounded-full ${cancelled ? "bg-slate-400" : "bg-emerald-600"}`} />
            {cancelled ? t("mm_status_canceled") : t("mm_status_active")}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-indigo-50/70 px-4 py-3">
            <p className="text-sm text-slate-500">{t("mm_price")}</p>
            <p className="mt-0.5 text-base font-bold text-slate-900">
              {formatMoney(sub.amount, sub.currency, language)}/{periodLabel(sub, t)}
            </p>
          </div>
          <div className="rounded-xl bg-indigo-50/70 px-4 py-3">
            <p className="text-sm text-slate-500">{cancelled ? t("mm_access_until") : t("mm_next_billing")}</p>
            <p className="mt-0.5 text-base font-bold text-slate-900">{formatDate(sub.currentPeriodEnd, language)}</p>
          </div>
        </div>
      </MmCard>

      {!cancelled && (
        <Link to={`/profile/subscription/${encodeURIComponent(sub.id)}/cancel`} className="mt-4 block">
          <MmCard className="flex items-center px-5 py-4 transition-colors hover:bg-slate-50">
            <span className="flex-1 text-base font-medium text-red-700">{t("mm_cancel_sub")}</span>
            <ChevronRight className="size-5 text-red-700" aria-hidden="true" />
          </MmCard>
        </Link>
      )}
    </div>
  );
}
